/**
 * Chat button tooltip component - helpful bubble explaining toggle functionality
 */

import React, { useState, useEffect } from 'react';
import { AgeProfile } from '../config/ageProfiles.config';

interface ChatButtonTooltipProps {
  isVisible: boolean;
  ageProfile: AgeProfile;
  position?: 'left' | 'right' | 'bottom';
  onComplete?: () => void;
}

export default function ChatButtonTooltip({ 
  isVisible, 
  ageProfile, 
  position = 'left',
  onComplete 
}: ChatButtonTooltipProps) {
  const [animationPhase, setAnimationPhase] = useState<'enter' | 'visible' | 'exit'>('enter');

  useEffect(() => {
    if (isVisible) {
      setAnimationPhase('enter');
      
      // Enter animation, then stay visible
      const enterTimer = setTimeout(() => {
        setAnimationPhase('visible');
      }, 300);
      
      // Auto-hide after 3 seconds
      const hideTimer = setTimeout(() => {
        setAnimationPhase('exit');
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
        }, 300);
      }, 3000);
      
      return () => {
        clearTimeout(enterTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isVisible, onComplete]);

  if (!isVisible && animationPhase !== 'exit') return null;

  // Age-appropriate tooltip message
  const getTooltipMessage = () => {
    const maxAge = ageProfile.age_range.max;
    if (maxAge <= 8) {
      return "💬 Click to see our chat!";
    } else if (maxAge <= 11) {
      return "💬 Toggle conversation panel";
    } else {
      return "💬 View conversation history";
    }
  };

  // Position classes
  const getPositionClasses = () => {
    switch (position) {
      case 'left':
        return {
          container: 'right-full mr-3',
          arrow: 'absolute top-1/2 -translate-y-1/2 -right-2 w-0 h-0 border-l-8 border-r-0 border-t-8 border-b-8 border-l-white/90 border-t-transparent border-b-transparent'
        };
      case 'right':
        return {
          container: 'left-full ml-3',
          arrow: 'absolute top-1/2 -translate-y-1/2 -left-2 w-0 h-0 border-r-8 border-l-0 border-t-8 border-b-8 border-r-white/90 border-t-transparent border-b-transparent'
        };
      case 'bottom':
        return {
          container: 'top-full mt-3',
          arrow: 'absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-t-0 border-b-white/90 border-l-transparent border-r-transparent'
        };
      default:
        return {
          container: 'right-full mr-3',
          arrow: 'absolute top-1/2 -translate-y-1/2 -right-2 w-0 h-0 border-l-8 border-r-0 border-t-8 border-b-8 border-l-white/90 border-t-transparent border-b-transparent'
        };
    }
  };

  const positionClasses = getPositionClasses();

  return (
    <div className={`absolute ${positionClasses.container} z-50 transition-all duration-300 ease-out ${
      animationPhase === 'enter' ? 'opacity-0 scale-75 translate-y-2' :
      animationPhase === 'exit' ? 'opacity-0 scale-75 translate-y-2' : 
      'opacity-100 scale-100 translate-y-0'
    }`}>
      
      {/* Tooltip bubble */}
      <div className="relative">
        <div className="bg-white/90 backdrop-blur-md text-gray-800 px-4 py-3 rounded-2xl shadow-2xl border border-white/20 whitespace-nowrap">
          <p className={`${ageProfile.theme.typography.bodySize} font-medium leading-tight`}>
            {getTooltipMessage()}
          </p>
        </div>
        
        {/* Arrow */}
        <div className={positionClasses.arrow} />
      </div>
      
      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-blue-400/20 blur-lg rounded-2xl -z-10 animate-pulse" />
    </div>
  );
}